/**
 * 🛏️ **The rúm in the world — the bed's model, one per bed (D133).**
 *
 * 🎯 haugur picked bed **D** (`Design/samples/162`): a plank bed on legs under
 * his teppi D. It was made from words by Rodin on Replicate and slimmed to its
 * colour map (`tools/slim-model.mjs --albedo 1024`) — 17 248 triangles, 2 MB.
 *
 * 🔴 **The house pattern, cut down** (`burmesh.ts`): loaded once, cloned per
 * bed, and re-dressed as a `MeshLambertNodeMaterial` over its own colour map
 * with the same daylight and torch lift — **never the file's own PBR
 * material**: a new material path once blacked out dev and staging. ⚠️ And the
 * slimmed file keeps metalness 1 with its map gone, which draws near-black —
 * the first icon found that.
 *
 * 📐 **The headboard is at the model's −z end** (measured: 0.41 tall there,
 * 0.13 at +z) — the same end as a house's door. So the house's turn,
 * `atan2(into.x, into.z)`, puts the headboard over the HEAD cell, the one the
 * player clicked, and the foot runs away from them.
 */

import { Box3, Group, Mesh, type Object3D, SRGBColorSpace, Vector3, type Texture } from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { burFrame, CHUNK_SIZE, isRum, rumCentre, rumFacing, type BurFacing } from '@lofi/shared';
import { burDaylight } from './burmesh.ts';

/** A bed's head, in chunk-local cells. */
export interface RumAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: BurFacing;
}

/** 📏 The bed is two blocks long; the model's long side is scaled to this. */
const BED_LENGTH = 2;
/** 🔦 Torch light added at full — the houses' own number. */
const TORCH_LIFT = 0.5;
/** 🔦 Steps of torch light, bounded so the number of materials stays five. */
const TORCH_STEPS = 4;

function setEmissive(material: MeshLambertNodeMaterial, node: unknown): void {
  (material as unknown as { emissiveNode: unknown }).emissiveNode = node;
}

/** Every bed head in one chunk's blocks. */
export function bedsIn(blocks: Uint16Array): RumAt[] {
  const out: RumAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array, as burmesh.ts does. */
    const id = blocks[i] ?? 0;
    if (!isRum(id)) continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      facing: rumFacing(id) ?? 'north',
    });
  }
  return out;
}

export class RumModel {
  private template: Object3D | null = null;
  private map: Texture | null = null;
  private readonly waiting: (() => void)[] = [];
  private readonly mats = new Map<number, MeshLambertNodeMaterial>();
  /** 🔬 What happened, for `window.__beds` — a bed nobody can see must say why. */
  readonly stats = {
    ready: false,
    map: false,
    placed: 0,
    waiting: 0,
    error: null as string | null,
  };

  constructor(url: string) {
    new GLTFLoader()
      .loadAsync(url)
      .then((gltf) => {
        const scene = gltf.scene;
        scene.traverse((o) => {
          if (o instanceof Mesh && this.map === null) {
            const m = (o.material as { map?: Texture | null }).map ?? null;
            this.map = m;
          }
        });
        const box = new Box3().setFromObject(scene);
        const size = box.getSize(new Vector3());
        const centre = box.getCenter(new Vector3());
        const s = BED_LENGTH / Math.max(size.z, 1e-6);
        scene.position.set(-centre.x, -box.min.y, -centre.z);
        const bed = new Group();
        bed.add(scene);
        bed.scale.setScalar(s);
        const wrap = new Group();
        wrap.add(bed);
        this.template = wrap;
        this.stats.ready = true;
        this.stats.map = this.map !== null;
        for (const fill of this.waiting.splice(0)) fill();
      })
      .catch((err: unknown) => {
        this.stats.error = err instanceof Error ? err.message : String(err);
        console.error('Rúm: the model did not load', err);
      });
  }

  /** The material for a bed in `light` (0–1) of added torch light. */
  private materialFor(light: number): MeshLambertNodeMaterial | null {
    const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
    const had = this.mats.get(step);
    if (had) return had;
    if (this.map === null) return null;
    this.map.colorSpace = SRGBColorSpace;
    const tile = texture(this.map, uv());
    const colour = vec3(tile.r, tile.g, tile.b);
    const made = new MeshLambertNodeMaterial();
    made.colorNode = colour;
    setEmissive(
      made,
      colour.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))),
    );
    this.mats.set(step, made);
    return made;
  }

  place(
    group: Group,
    beds: readonly RumAt[],
    litAt: (x: number, y: number, z: number) => number = () => 0,
  ): void {
    const fill = (): void => {
      if (this.template === null) return;
      for (const bed of beds) {
        const copy = this.template.clone(true);
        const { into } = burFrame(bed.facing);
        const c = rumCentre(bed, bed.facing);
        copy.position.set(c.x + 0.5, c.y, c.z + 0.5);
        copy.rotation.y = Math.atan2(into.x, into.z);
        /* 🔦 Asked a cell ABOVE the head: the head's own cell is solid, and a
           solid cell's light is 0 — the houses learned that one first. */
        const mat = this.materialFor(litAt(bed.x, bed.y + 1, bed.z));
        if (mat !== null) {
          copy.traverse((o) => {
            if (o instanceof Mesh) o.material = mat;
          });
        }
        group.add(copy);
        this.stats.placed += 1;
      }
    };
    if (this.template !== null) fill();
    else {
      this.waiting.push(fill);
      this.stats.waiting = this.waiting.length;
    }
  }
}

/** One group per chunk, or `null` when the chunk has no bed in it. */
export function buildRumGroup(
  beds: readonly RumAt[],
  model: RumModel,
  litAt?: (x: number, y: number, z: number) => number,
): Group | null {
  if (beds.length === 0) return null;
  const group = new Group();
  model.place(group, beds, litAt);
  return group;
}
